import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';

/**
 * The route the whole Depart section is looking at.
 *
 * Retail scopes its city in the URL (/retail/:city/...), which works because
 * the city list is fixed and short. Depart routes are user-created and
 * open-ended, so putting an id in every path would make links fragile the
 * moment a route is deleted, and would not survive being shared between users
 * who follow different routes. It lives in context instead, persisted to
 * localStorage so the choice survives a reload — the same approach
 * WorkspaceContext takes for the active workspace.
 *
 * One selection governs Dashboard, Engines, Smart Engines and Performance, so
 * switching route in the topbar switches all four rather than each page
 * carrying its own dropdown that silently disagrees with the others.
 *
 * `routes` is what the signed-in user actually follows — their subscriptions,
 * not every route in the system. The full catalogue is still fetched, but only
 * to resolve a subscription's route_id into a full route object; it is never
 * shown as a list the user did not choose.
 */

const STORAGE_KEY = 'genie.depart.route';

const Ctx = createContext(null);

export function DepartRouteProvider({ children }) {
  const [routes, setRoutes] = useState([]);   // what this user follows
  const [routeId, setRouteIdState] = useState(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isInteger(n) ? n : null;
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [subs, all] = await Promise.all([
        api.getDepartSubscriptions().catch(() => []),
        api.getDepartRoutes().catch(() => []),
      ]);
      const catalogue = Array.isArray(all) ? all : [];
      // Subscriptions carry route_id, not id — resolve them against the
      // catalogue so the rest of the app always handles route-shaped objects.
      const followed = (Array.isArray(subs) ? subs : [])
        .map((sb) => catalogue.find((r) => r.id === sb.route_id) || {
          id: sb.route_id, origin: sb.origin, destination: sb.destination,
          label: sb.label, currency: sb.currency, horizon_days: sb.horizon_days ?? 30,
        })
        .filter(Boolean);

      setRoutes(followed);
      // Fall back to the first followed route when the stored one is gone —
      // an unfollowed or deleted id would otherwise leave every page blank.
      setRouteIdState((cur) => (followed.some((r) => r.id === cur) ? cur : (followed[0]?.id ?? null)));
      return followed;
    } catch {
      setRoutes([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const setRouteId = useCallback((id) => {
    setRouteIdState(id);
    if (id == null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(id));
  }, []);

  const route = useMemo(() => routes.find((r) => r.id === routeId) || null, [routes, routeId]);

  const value = useMemo(
    () => ({ routes, route, routeId, setRouteId, refresh, loading }),
    [routes, route, routeId, setRouteId, refresh, loading],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDepartRoute() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDepartRoute must be used inside <DepartRouteProvider>');
  return v;
}

export function routeLabel(route) {
  if (!route) return '—';
  // User-created routes carry no label; derive one from the pair.
  return route.label || `${route.origin} → ${route.destination}`;
}
